import React from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
	Accordion,
	Alert,
	Box,
	Button,
	createModal,
	createStyles,
	Divider,
	Flex,
	Icon,
	List,
	Loader,
	Stack,
	TFText,
	Tooltip,
	UnstyledButton,
	useDisclosure,
} from '@tf/ui';
import { fmt, splitObjectId } from '@tf/utils';

import {
	useAddPaymentAssessmentJobMutation,
	useInvalidateQueryOnJobCompletion,
	useTRAJobStatus,
} from '@/core/api/payments';
import type {
	AssessmentCalculationLog,
	Decision,
	Indicator,
	PaymentStatus,
	ScreeningResponse,
	TransactionAssessment,
} from '@/core/types/payments';
import { groupScreeningMatches } from '@/components/payments/groupScreeningMatches';
import {
	extractAssessmentData,
	getAssessmentAlertConfig,
	getDecisionStatus,
	getRiskBadge,
	getRiskLevelMessage,
} from '@/components/utils';

const useStyles = createStyles(({ colors }) => ({
	chip: {
		fontSize: 14,
		height: 20,
		display: 'flex',
		alignItems: 'center',
		color: colors.gray[9],
		background: '#f9a8a8',
		borderRadius: 12,
		padding: '0 8px',
		'&:hover': {
			filter: 'brightness(.95)',
		},
	},
}));

interface Props {
	status: PaymentStatus;
	decision?: Decision;
	paymentId: string;
	assessmentResult: TransactionAssessment[];
}

export const TransactionAssessmentResult: React.FC<Props> = ({
	status,
	decision,
	paymentId,
	assessmentResult,
}) => {
	const assessmentResultModal = useDisclosure();

	const addPaymentAssessmentJobMutation = useAddPaymentAssessmentJobMutation();
	const lastAssessment = assessmentResult?.[0];

	const { actions, messages, manualReviewNeeded, errorDetails, errorMessage } =
		extractAssessmentData(lastAssessment);

	const { data, refetch: refetchTRAJob } = useTRAJobStatus(paymentId);
	const isAssessmentInProgress =
		data?.meta['@status'] === 'IN_PROGRESS' || data?.meta['@status'] === 'PENDING';

	useInvalidateQueryOnJobCompletion(!isAssessmentInProgress, [
		'events',
		'payment',
		'assessment-results',
	]);

	const decisionStatus = getDecisionStatus(status);
	const riskMessage = getRiskLevelMessage(lastAssessment, decisionStatus, decision);
	const {
		color: alertColor,
		icon: alertIcon,
		title: alertTitle,
	} = getAssessmentAlertConfig(riskMessage, decisionStatus, errorMessage, manualReviewNeeded);

	return (
		<Box p="16px">
			<Alert
				title={alertTitle}
				icon={alertIcon}
				color="dark"
				fw="500"
				fz="16px"
				styles={{
					title: { fontSize: '16px', fontWeight: '600' },
					message: { fontSize: '14px', fontWeight: '400', color: '#374151' },
					icon: { color: `rgba(${alertColor})` },
					root: { backgroundColor: `rgba(${alertColor}, 0.2)` },
					body: { gap: '4px' },
				}}
				sx={{
					backgroundColor: `rgba(${alertColor}, 0.2)`,
					borderColor: `rgba(${alertColor}, 0.5)`,
				}}
			>
				{riskMessage.description}
			</Alert>
			<ItemList items={messages} />
			<TFText fz="16px" lh="18px" fw="600" mb="10px" mt="20px">
				Required actions
			</TFText>
			<ItemList items={actions} />
			<ItemList items={errorDetails} />
			<Flex gap="8px" mt="12px">
				{!decisionStatus && (
					<Button
						variant="light"
						size="sm"
						leftSection={isAssessmentInProgress ? <Loader size={14} /> : <Icon.IconRefresh size={18} />}
						disabled={isAssessmentInProgress}
						onClick={async () => {
							await addPaymentAssessmentJobMutation.mutateAsync({ paymentId });
							await refetchTRAJob();
						}}
					>
						{isAssessmentInProgress ? 'In progress' : 'Reassess'}
					</Button>
				)}
				<Button
					variant="light"
					size="sm"
					leftSection={<Icon.IconFileDescription size={18} />}
					onClick={assessmentResultModal.open}
				>
					Assessment results
				</Button>
			</Flex>
			<AssessmentResultsModal
				size="800"
				withinPortal
				opened={assessmentResultModal.isOpen}
				onClose={assessmentResultModal.close}
				title="Assessment results"
				contentProps={{ assessmentResult }}
			/>
		</Box>
	);
};

const Section = ({
	title,
	indicators,
	groupScore,
}: {
	title: string;
	indicators: Record<string, Indicator>;
	groupScore: number;
}) => {
	return (
		<Box>
			<TFText m="8px 0" fw="600" fz="16px">
				{title}
			</TFText>
			{Object.entries(indicators)
				.sort(([, a], [, b]) => a.order - b.order)
				.map(([key, { risk_level, calculation, text }]) => {
					return (
						<React.Fragment key={key}>
							<Flex align="center" gap="sm" m="4px 0">
								<Box fz="14px" flex={1}>
									{text}
								</Box>
								<Box fw="500" fz="14px">
									<Flex align="center" gap="4px">
										Score: {getRiskBadge(risk_level, calculation.base_score)}
										<Tooltip label={calculation.text} position="top">
											<Icon.IconInfoCircle style={{ opacity: 0.5 }} size={16} />
										</Tooltip>
									</Flex>
								</Box>
							</Flex>
							<Divider w="100%" size="1px" color="#E5E7EB" />
						</React.Fragment>
					);
				})}
			<Box fw="500" fz="14px" mt="4px">
				Section score: {groupScore}
			</Box>
		</Box>
	);
};

export const LastAssessmentResult = ({
	assessmentResult,
}: {
	assessmentResult: TransactionAssessment[];
}) => {
	const lastAssessment = assessmentResult?.[0];
	const calculationLog = lastAssessment.data.calculation_log;
	const screeningResponse = lastAssessment.data.raw_screening_response;
	const { objectName: paymentUuid } = splitObjectId(lastAssessment?.id);

	return (
		<Stack m={`24px 12px ${calculationLog ? 0 : 12}px 12px`}>
			<AssessmentSummary
				title={`Summary of the most recent of ${assessmentResult?.length} assessments`}
				assessment={lastAssessment}
			/>
			{calculationLog && (
				<Accordion chevronPosition="left">
					<Accordion.Item value="details">
						<Accordion.Control>Assessment details</Accordion.Control>
						<Accordion.Panel>
							<AssessmentDetails
								screeningResponse={screeningResponse}
								calculationLog={calculationLog}
								assessmentId={paymentUuid}
							/>
						</Accordion.Panel>
					</Accordion.Item>
				</Accordion>
			)}
		</Stack>
	);
};

export const AssessmentSummary: React.FC<{
	assessment: TransactionAssessment;
	title?: string;
}> = ({ assessment, title = 'Assessment summary' }) => {
	const { riskLevel, riskScore, manualReviewNeeded, errorDetails, errorMessage } =
		extractAssessmentData(assessment);

	if (errorMessage)
		return (
			<Stack
				gap="2px"
				bg="#ffe3e3"
				p="8px 16px"
				sx={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
			>
				<Flex gap="4px" mb="4px" align="center">
					<TFText fw="600" fz="16px">
						{errorMessage}
					</TFText>
				</Flex>
				<ItemList items={errorDetails} />
			</Stack>
		);

	if (riskLevel) {
		return (
			<Stack gap="2px" bg="#eff6ff" p="8px" sx={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}>
				<Flex gap="4px" mb="8px" align="center">
					<TFText fw="600" fz="16px">
						{title}
					</TFText>
					<Tooltip label="The assessment was made using the company’s methodology" position="top">
						<Icon.IconInfoCircle style={{ opacity: 0.5 }} size={16} />
					</Tooltip>
				</Flex>
				<Box fw="500" fz="14px">
					<Flex gap="6px">Overall risk level: {getRiskBadge(riskLevel)}</Flex>
				</Box>
				{Boolean(riskScore) && (
					<Box fw="500" fz="14px">
						Overall risk score: {riskScore}
					</Box>
				)}
				<Box fw="500" fz="14px">
					{manualReviewNeeded ? 'Manual review needed' : 'Manual review not needed'}
				</Box>
			</Stack>
		);
	}

	return null;
};

const sectionsOrder = [
	'transaction_amount_and_frequency',
	'actor_risk',
	'client_risk_profile',
	'purpose_and_documentation',
	'actor_screening',
] as const;

export const AssessmentDetails: React.FC<{
	calculationLog?: AssessmentCalculationLog;
	screeningResponse?: ScreeningResponse;
	assessmentId: string;
}> = ({ calculationLog, assessmentId, screeningResponse }) => {
	const actorMatches =
		screeningResponse?.actor.rawResponse.hits?.map((item) => ({
			name: item.doc.name ?? 'N/A',
			id: item.doc.id,
			doc: item.doc,
		})) ?? [];

	const bankMatches =
		screeningResponse?.bank.rawResponse.hits?.map((item) => ({
			name: item.doc.name ?? 'N/A',
			id: item.doc.id,
			doc: item.doc,
		})) ?? [];

	const allMatches = [...actorMatches, ...bankMatches];
	const actorGroupedMatches = groupScreeningMatches(actorMatches);
	const bankGroupedMatches = groupScreeningMatches(bankMatches);

	return (
		<>
			{calculationLog &&
				sectionsOrder.map((section) => {
					if (calculationLog[section]) {
						const { group_score, description, ...indicators } = calculationLog[section];
						return (
							<Section
								key={section}
								title={description as any}
								groupScore={group_score}
								indicators={indicators as any}
							/>
						);
					}
					return null;
				})}

			{Boolean(allMatches.length) && (
				<Box>
					<TFText m="8px 0" fw="600" fz="16px">
						Screening matches
					</TFText>
					<ScreeningMatchesGroup
						assessmentId={assessmentId}
						groupName="Actor"
						groupedMatches={actorGroupedMatches}
					/>
					<ScreeningMatchesGroup
						assessmentId={assessmentId}
						groupName="Bank"
						groupedMatches={bankGroupedMatches}
					/>
				</Box>
			)}
		</>
	);
};

const AssessmentResultsModal = createModal(
	({ assessmentResult }: { assessmentResult: TransactionAssessment[] }) => {
		const lastAssessment = assessmentResult?.[0];
		const [expandedValue, setExpandedValue] = React.useState<string | null>(lastAssessment?.id || null);

		return (
			<Box>
				<Accordion
					variant="contained"
					value={expandedValue}
					onChange={setExpandedValue}
					chevronPosition="left"
				>
					{assessmentResult.map((assessment) => {
						const { meta, data, id } = assessment;
						const { riskLevel, riskScore } = extractAssessmentData(assessment);
						const createdAt = fmt.toDate(meta['@createdAt'], { preset: 'full_date_with_time' });
						const calculationLog = data.calculation_log;
						const screeningResponse = data.raw_screening_response;
						const isExpanded = expandedValue === id;
						const { objectName: paymentUuid } = splitObjectId(id);

						return (
							<Accordion.Item key={id} value={id}>
								<Accordion.Control>
									<Flex align="center" justify="space-between" style={{ width: '100%' }}>
										<span>{`Result from ${createdAt}`}</span>
										{!isExpanded && riskLevel && (
											<Flex align="center" gap="12px">
												{Boolean(riskScore) && (
													<TFText fw="500" fz="14px">
														Risk score: {riskScore}
													</TFText>
												)}
												{getRiskBadge(riskLevel)}
											</Flex>
										)}
									</Flex>
								</Accordion.Control>
								<Accordion.Panel>
									<Stack>
										<AssessmentSummary assessment={assessment} />
										<AssessmentDetails
											calculationLog={calculationLog}
											assessmentId={paymentUuid}
											screeningResponse={screeningResponse}
										/>
									</Stack>
								</Accordion.Panel>
							</Accordion.Item>
						);
					})}
				</Accordion>
			</Box>
		);
	}
);

const ScreeningMatchesGroup = ({
	groupName,
	groupedMatches,
	assessmentId,
}: {
	groupName: string;
	groupedMatches: {
		pep: any[];
		sanctioned: any[];
		adverseMedia: any[];
		hasMatches: boolean;
	};
	assessmentId: string;
}) => {
	const { classes } = useStyles();
	const { paymentId: paymentObjectName } = useParams<{ paymentId: string }>();
	const navigate = useNavigate();

	if (!groupedMatches.hasMatches) return null;

	const handleClick = (filter: string) => {
		navigate({
			pathname: `/payments/${paymentObjectName}/assessments/${assessmentId}/screenings`,
			search: `?${new URLSearchParams({ type: groupName.toLowerCase(), filter })}`,
		});
	};

	return (
		<Flex mt="4px" align="center" gap="0 8px">
			<TFText fz="14px">{groupName}:</TFText>
			{groupedMatches.pep.length > 0 && (
				<UnstyledButton
					onClick={() => handleClick('pep')}
					className={classes.chip}
				>{`Is peped (${groupedMatches.pep.length})`}</UnstyledButton>
			)}
			{groupedMatches.sanctioned.length > 0 && (
				<UnstyledButton
					onClick={() => handleClick('sanction')}
					className={classes.chip}
				>{`Is sanctioned (${groupedMatches.sanctioned.length})`}</UnstyledButton>
			)}
			{groupedMatches.adverseMedia.length > 0 && (
				<UnstyledButton
					onClick={() => handleClick('adverseMedia')}
					className={classes.chip}
				>{`Is adverse media (${groupedMatches.adverseMedia.length})`}</UnstyledButton>
			)}
		</Flex>
	);
};

const ItemList: React.FC<{ items?: string[] }> = ({ items }) => {
	if (!items?.length) return null;

	return (
		<List mt="12px">
			{items.map((item) => (
				<List.Item mr="8px" key={item}>
					{item}
				</List.Item>
			))}
		</List>
	);
};
